/**
 * A fila de pendências.
 *
 * É a tela que substitui o desaparecimento silencioso do R, onde uma data em
 * formato inesperado virava `NA` e sumia do resultado. Cada linha aqui é uma
 * que não entrou, com o valor que causou o problema e a linha exata da
 * planilha — o número que o Excel mostra, para o operador ir conferir.
 *
 * O filtro abre em "abertas e acionáveis" de propósito. `SEM_VALOR` responde
 * por milhares de linhas nos arquivos reais, e uma fila que ninguém consegue
 * zerar é uma fila que ninguém olha.
 */

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Botao } from '../../components/Botao'
import { Cartao } from '../../components/Cartao'
import { Selecao } from '../../components/Campo'
import { Carregando, Erro, Vazio } from '../../components/Estado'
import { BarraFiltros, Pagina } from '../../components/Pagina'
import { Tabela, type Coluna } from '../../components/Tabela'
import { contagem } from '../../lib/formato'
import type { MotivoPendencia, Pendencia } from '../../lib/tipos'
import { usePendencias } from './hooks'
import estilos from './Ingestao.module.css'

const MOTIVOS: { valor: MotivoPendencia | ''; rotulo: string }[] = [
  { valor: '', rotulo: 'Todos os motivos' },
  { valor: 'produto', rotulo: 'Produto sem de-para' },
  { valor: 'distribuidora', rotulo: 'Distribuidora sem de-para' },
  { valor: 'praca', rotulo: 'Praça sem de-para' },
  { valor: 'base', rotulo: 'Base de suprimento sem cadastro' },
  { valor: 'data', rotulo: 'Data ilegível' },
  { valor: 'numero', rotulo: 'Número ilegível' },
  { valor: 'obrigatorio', rotulo: 'Campo obrigatório vazio' },
  { valor: 'sem_valor', rotulo: 'Sem valor coletado' },
  { valor: 'duplicata', rotulo: 'Chave repetida com valor diferente' },
  { valor: 'outro', rotulo: 'Outro' },
]

/** De-para que resolve cada motivo — o atalho da fila para o cadastro. */
const CADASTRO: Partial<Record<MotivoPendencia, { rota: string; rotulo: string }>> = {
  produto: { rota: '/de-paras?tipo=produto', rotulo: 'Cadastrar de-para de produto' },
  distribuidora: {
    rota: '/de-paras?tipo=distribuidora',
    rotulo: 'Cadastrar de-para de distribuidora',
  },
  praca: { rota: '/de-paras?tipo=praca', rotulo: 'Cadastrar de-para de praça' },
}

export function PendenciasPage() {
  const [parametros, setParametros] = useSearchParams()
  const [pagina, setPagina] = useState(1)

  const upload = parametros.get('upload')
  const motivo = (parametros.get('motivo') ?? '') as MotivoPendencia | ''
  const situacao = parametros.get('situacao') ?? 'abertas'

  const consulta = usePendencias({
    upload: upload ? Number(upload) : undefined,
    motivo: motivo || undefined,
    resolvida: situacao === 'todas' ? undefined : situacao === 'resolvidas',
    pagina,
  })

  function ajustar(chave: string, valor: string) {
    const proximo = new URLSearchParams(parametros)
    if (valor) proximo.set(chave, valor)
    else proximo.delete(chave)
    setParametros(proximo, { replace: true })
    setPagina(1)
  }

  const colunas: Coluna<Pendencia>[] = [
    {
      chave: 'origem',
      cabecalho: 'Origem',
      largura: '190px',
      celula: (item) => (
        <div className={estilos.arquivo}>
          <span className={estilos.arquivoNome}>{item.aba || '—'}</span>
          <span className={estilos.arquivoMeta}>
            linha {contagem(item.linhaOrigem)} ·{' '}
            <Link to={`/uploads/${item.upload}`}>upload {item.upload}</Link>
          </span>
        </div>
      ),
    },
    {
      chave: 'motivo',
      cabecalho: 'Motivo',
      largura: '210px',
      // `motivoLabel` vem do backend de propósito — o frontend não mantém cópia
      // do vocabulário de motivos.
      celula: (item) => item.motivoLabel,
    },
    {
      chave: 'valor',
      cabecalho: 'Valor com problema',
      celula: (item) => (
        <div className={estilos.problema}>
          {/* Texto vindo de planilha preenchida em campo. O React escapa por
              construção — no R isso era concatenado em HTML com `paste0`. */}
          <code className={estilos.valorBruto}>{item.valorProblema || '(vazio)'}</code>
          {item.campo && <span className={estilos.arquivoMeta}>coluna: {item.campo}</span>}
        </div>
      ),
    },
    {
      chave: 'detalhe',
      cabecalho: 'Detalhe',
      celula: (item) => <span className={estilos.detalhe}>{item.detalhe || '—'}</span>,
    },
    {
      chave: 'acao',
      cabecalho: '',
      largura: '190px',
      celula: (item) => {
        const cadastro = CADASTRO[item.motivo]
        if (!cadastro || item.resolvida) return null
        return (
          <Link className={estilos.acaoLink} to={cadastro.rota}>
            {cadastro.rotulo} →
          </Link>
        )
      },
    },
  ]

  const total = consulta.data?.count ?? 0
  const porPagina = 50
  const paginas = Math.max(1, Math.ceil(total / porPagina))

  return (
    <Pagina
      titulo="Pendências"
      descricao="Linhas que não puderam ser normalizadas. Cada uma guarda o conteúdo cru e a linha de origem, e volta ao fluxo quando o de-para que faltava é criado — sem novo upload."
    >
      <BarraFiltros>
        <Selecao
          rotulo="Motivo"
          valor={motivo}
          aoMudar={(valor) => ajustar('motivo', valor)}
          opcoes={MOTIVOS.filter((m) => m.valor !== '').map((m) => ({
            valor: m.valor,
            rotulo: m.rotulo,
          }))}
          vazioRotulo="Todos os motivos"
        />
        <Selecao
          rotulo="Situação"
          valor={situacao}
          aoMudar={(valor) => ajustar('situacao', valor)}
          opcoes={[
            { valor: 'abertas', rotulo: 'Abertas' },
            { valor: 'resolvidas', rotulo: 'Resolvidas' },
            { valor: 'todas', rotulo: 'Todas' },
          ]}
        />
        {upload && (
          <div className={estilos.recorte}>
            <span className={estilos.recorteRotulo}>Recortado no upload {upload}</span>
            <Botao tamanho="pequeno" variante="sutil" onClick={() => ajustar('upload', '')}>
              Ver de todos os uploads
            </Botao>
          </div>
        )}
      </BarraFiltros>

      {consulta.isPending && !consulta.data && <Carregando rotulo="Carregando a fila…" />}
      {consulta.isError && <Erro erro={consulta.error} aoTentarDeNovo={() => consulta.refetch()} />}

      {consulta.data && (
        <Cartao
          titulo={`${contagem(total)} pendência(s)`}
          subtitulo="Motivo acionável resolve cadastrando um de-para. Os demais são informativos — a célula veio vazia da coleta e ninguém no escritório consegue preenchê-la."
          acoes={
            paginas > 1 ? (
              <div className={estilos.paginacao}>
                <Botao
                  tamanho="pequeno"
                  disabled={pagina <= 1}
                  onClick={() => setPagina((p) => p - 1)}
                >
                  Anterior
                </Botao>
                <span className={estilos.paginaContador}>
                  {pagina} de {paginas}
                </span>
                <Botao
                  tamanho="pequeno"
                  disabled={pagina >= paginas}
                  onClick={() => setPagina((p) => p + 1)}
                >
                  Próxima
                </Botao>
              </div>
            ) : undefined
          }
        >
          {consulta.data.results.length === 0 ? (
            <Vazio titulo="Nada nesta fila">
              Nenhuma pendência com este recorte. Se acabou de criar um de-para, reprocesse o
              upload para que as linhas voltem ao fluxo.
            </Vazio>
          ) : (
            <Tabela
              colunas={colunas}
              linhas={consulta.data.results}
              chaveDe={(item) => item.id}
              rotulo="Fila de pendências"
            />
          )}
        </Cartao>
      )}
    </Pagina>
  )
}
